import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography } from '@mui/material';
import { v4API } from '../../Api';
import { actionKey, errorMessage } from './common';
import { useV4 } from './V4Layout';
import { payoutDateRange, payoutUnits } from './Payouts';

const storageKey = 'v4-processing-settlement';
const empty = { items: [], total: 0 };
const rate = value => value == null ? 'Ставка не задана' : `${value / 100} %`;
const group = row => `${row.processingName || row.processingId} · ${rate(row.commissionBps)}`;
const types = { accrual: 'Начисление', settlement: 'Погашение', reversal: 'Отмена погашения' };
function readPending() {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (!value.key || !value.body?.accountId || !value.account || value.body.confirm !== 'processing-settlement') throw new Error();
    return value;
  } catch { return { unreadable: true }; }
}
function Pages({ data, page, change, disabled }) {
  return <TablePagination component="div" count={data.total} page={page - 1} rowsPerPage={25} rowsPerPageOptions={[25]}
    onPageChange={(_, next) => change(next + 1)} disabled={disabled} labelRowsPerPage="На странице"
    labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`} />;
}
export default function ProcessingDebts() {
  const { system } = useV4();
  const [accounts, setAccounts] = useState(empty);
  const [operations, setOperations] = useState(empty);
  const [summary, setSummary] = useState(null);
  const [accountPage, setAccountPage] = useState(1);
  const [operationPage, setOperationPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [draft, setDraft] = useState({ processingId: '', from: '', to: '' });
  const [selectedAccount, setSelectedAccount] = useState('');
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState(readPending);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ amountRUB: '', amountUSDT: '', reason: '' });
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    const historyFilters = { ...filters, ...(selectedAccount && { accountId: selectedAccount }) };
    Promise.all([
      v4API.processingDebtAccounts({ ...(filters.processingId && { processingId: filters.processingId }), page: accountPage, limit: 25 }),
      v4API.processingDebtOperations({ ...historyFilters, page: operationPage, limit: 25 }),
      v4API.processingDebtSummary(historyFilters),
    ]).then(([a, o, s]) => { if (active) { setAccounts(a); setOperations(o); setSummary(s); } })
      .catch(err => { if (active) { setError(errorMessage(err)); setAccounts(empty); setOperations(empty); setSummary(null); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, selectedAccount, accountPage, operationPage, reload]);
  const open = (account, reversalOf) => {
    setError(''); setConfirmed(false); setForm({ amountRUB: '', amountUSDT: '', reason: '' });
    setDialog({ account, reversalOf });
  };
  const reverse = async operation => {
    if (lock.current || pending) return;
    lock.current = true; setBusy(true);
    try {
      const data = await v4API.processingDebtAccounts({ accountId: operation.accountId });
      if (!data.items[0]) throw new Error('Счёт не найден');
      open(data.items[0], operation._id);
    } catch (err) { setError(errorMessage(err)); }
    finally { lock.current = false; setBusy(false); }
  };
  const amount = dialog ? payoutUnits(form.amountRUB, dialog.account.scale) : null;
  const valid = dialog && (dialog.reversalOf || (amount !== null && amount > 0n &&
    amount <= payoutUnits(dialog.account.balance, dialog.account.scale) &&
    /^(0|[1-9]\d*)(?:\.\d{1,2})?$/.test(form.amountRUB) &&
    /^(0|[1-9]\d*)(?:\.\d{1,8})?$/.test(form.amountUSDT) && payoutUnits(form.amountUSDT, 8) > 0n)) &&
    !!form.reason.trim() && form.reason.trim().length <= 1000;
  const save = async () => {
    if (lock.current || !system?.ready || !confirmed || (!pending && !valid)) return;
    lock.current = true; setBusy(true); setError('');
    let request = pending;
    try {
      if (!request) {
        request = { key: actionKey(), account: dialog.account, body: {
          accountId: dialog.account._id, expectedRevision: dialog.account.revision, confirm: 'processing-settlement',
          reason: form.reason.trim(), ...(dialog.reversalOf ? { reversalOf: dialog.reversalOf } :
            { amountRUB: form.amountRUB, amountUSDT: form.amountUSDT }),
        } };
        sessionStorage.setItem(storageKey, JSON.stringify(request));
        setPending(request);
      }
      const result = await v4API.recordProcessingSettlement(request.body, request.key);
      sessionStorage.removeItem(storageKey); setPending(null); setDialog(null);
      setNotice(`Сохранено: ${types[result.operation.type]} · ${result.operation.amount} RUB · ${result.operation.amountUSDT} USDT${result.replayed ? ' (повтор без дублирования)' : ''}`);
      setReload(value => value + 1);
    } catch (err) {
      const status = err?.response?.status;
      if (!pending && [400, 409, 422].includes(status)) {
        sessionStorage.removeItem(storageKey); setPending(null); setDialog(null); setReload(value => value + 1);
      }
      setError(errorMessage(err));
    } finally { lock.current = false; setBusy(false); }
  };
  return <Stack spacing={2}>
    <Typography variant="h5" component="h2">Долги процессингов</Typography>
    <Alert severity="info">Долг — вся сумма оплаты клиента. Группы объединяют методы одного процессинга с одинаковой сохранённой комиссией. Изменение ставки не переносит старый долг. Получено USDT — фактический приход, без автоматического пересчёта RUB.</Alert>
    <Paper component="form" sx={{ p: 2 }} onSubmit={event => {
      event.preventDefault();
      try {
        setFilters({ ...(draft.processingId.trim() && { processingId: draft.processingId.trim() }), ...payoutDateRange(draft.from, draft.to) });
        setAccountPage(1); setOperationPage(1); setSelectedAccount(''); setError('');
      } catch (err) { setError(err.message); }
    }}><Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <TextField label="ID процессинга" value={draft.processingId} onChange={e => setDraft({ ...draft, processingId: e.target.value })} />
      <TextField label="Дата с (UTC)" type="date" InputLabelProps={{ shrink: true }} value={draft.from} onChange={e => setDraft({ ...draft, from: e.target.value })} />
      <TextField label="Дата по (UTC)" type="date" InputLabelProps={{ shrink: true }} value={draft.to} onChange={e => setDraft({ ...draft, to: e.target.value })} />
      <Button type="submit" disabled={loading}>Применить</Button>
      <Button disabled={loading || busy} onClick={() => { setError(''); setReload(v => v + 1); }}>Обновить</Button>
    </Stack></Paper>
    <Typography variant="body2">Остатки текущие. Даты фильтруют журнал и статистику погашений; отмены учитываются по дате отмены.</Typography>
    {error && <Alert severity="error">{error}</Alert>}{notice && <Alert severity="success">{notice}</Alert>}
    {pending && <Alert severity="warning" action={!pending.unreadable && <Button disabled={busy} onClick={() => {
      setDialog({ account: pending.account, reversalOf: pending.body.reversalOf }); setConfirmed(false);
    }}>Открыть сохранённый запрос</Button>}>
      {pending.unreadable ? 'Не удалось прочитать сохранённую операцию. Проверьте историю и восстановите данные браузера; новые операции заблокированы.' :
        'Есть незавершённый запрос. Повторите его с теми же данными, чтобы узнать результат без повторного списания.'}
    </Alert>}
    {loading && <LinearProgress />}
    {summary?.balances?.map(row => <Typography key={`${row.currency}:${row.scale}`} variant="h6">
      Общий долг{selectedAccount ? ' выбранной группы' : ''}: {row.balance} {row.currency} (точность {row.scale})
    </Typography>)}
    <TableContainer component={Paper}><Table size="small" aria-label="Долги процессингов"><TableHead><TableRow>
      {['Процессинг / ставка', 'Начислено', 'Погашено', 'Остаток', 'Получено USDT', 'Действия'].map(x => <TableCell key={x}>{x}</TableCell>)}
    </TableRow></TableHead><TableBody>{accounts.items.map(row => <TableRow key={row._id}>
      <TableCell>{group(row)}<br />{row.processingId}<br />{row.currency} / точность {row.scale}</TableCell>
      <TableCell>{row.accrued} {row.currency}</TableCell><TableCell>{row.settled} {row.currency}</TableCell>
      <TableCell>{row.balance} {row.currency}</TableCell><TableCell>{row.receivedUSDT}</TableCell>
      <TableCell><Button onClick={() => { setSelectedAccount(row._id); setOperationPage(1); }}>История группы</Button>
        <Button disabled={loading || busy || !!pending || !system?.ready || row.currency !== 'RUB' || payoutUnits(row.balance, row.scale) <= 0n}
          onClick={() => open(row)}>Записать погашение</Button></TableCell>
    </TableRow>)}{!accounts.items.length && <TableRow><TableCell colSpan={6}>Начислений пока нет</TableCell></TableRow>}</TableBody></Table></TableContainer>
    <Pages data={accounts} page={accountPage} change={setAccountPage} disabled={loading} />
    {selectedAccount && <Button onClick={() => { setSelectedAccount(''); setOperationPage(1); }}>Показать историю всех групп</Button>}
    {summary && <Paper sx={{ p: 2 }}>Погашено за период с учётом отмен: {summary.amountRUB} RUB · Получено: {summary.amountUSDT} USDT</Paper>}
    <Typography variant="h6">Журнал начислений и погашений</Typography>
    <TableContainer component={Paper}><Table size="small" aria-label="Журнал долгов"><TableHead><TableRow>
      {['Дата / группа', 'Операция', 'RUB / валюта', 'USDT', 'Остаток после', 'Заказ / маршрут', 'Автор / причина', 'Действие'].map(x => <TableCell key={x}>{x}</TableCell>)}
    </TableRow></TableHead><TableBody>{operations.items.map(row => <TableRow key={row._id}>
      <TableCell>{row.createdAt}<br />{group(row)}</TableCell><TableCell>{types[row.type]}<br />{row._id}{row.reversalOf && <><br />Отмена {row.reversalOf}</>}</TableCell>
      <TableCell>{row.amount} {row.currency}</TableCell><TableCell>{row.type === 'accrual' ? '—' : row.amountUSDT}</TableCell><TableCell>{row.balanceAfter} {row.currency}</TableCell>
      <TableCell>{row.orderId && <Link to={`/v4/orders/${encodeURIComponent(row.orderId)}`}>{row.orderId}</Link>}<br />{row.method} {row.routeId}</TableCell>
      <TableCell>{row.actor}<br />{row.reason}</TableCell><TableCell>{row.type === 'settlement' && (row.reversed ? 'Отменено' :
        <Button disabled={busy || !!pending || !system?.ready} onClick={() => reverse(row)}>Отменить погашение</Button>)}</TableCell>
    </TableRow>)}</TableBody></Table></TableContainer>
    <Pages data={operations} page={operationPage} change={setOperationPage} disabled={loading} />
    {dialog && <Dialog open onClose={() => { if (!busy) setDialog(null); }} fullWidth maxWidth="sm">
      <DialogTitle>{dialog.reversalOf ? 'Отмена погашения' : 'Погашение долга'} — {group(dialog.account)}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <Typography>Остаток: {dialog.account.balance} RUB</Typography>
        {pending ? <Alert severity="warning">Повтор сохранённой операции: {pending.body.reversalOf ? `отмена ${pending.body.reversalOf}` : `${pending.body.amountRUB} RUB / ${pending.body.amountUSDT} USDT`}. Причина: {pending.body.reason}</Alert> : <>
          {!dialog.reversalOf && <>
            <TextField label="Погасить RUB" value={form.amountRUB} disabled={busy} onChange={e => { setForm({ ...form, amountRUB: e.target.value }); setConfirmed(false); }} />
            <TextField label="Получено USDT" value={form.amountUSDT} disabled={busy} helperText="Фактическая сумма поступления, до 8 знаков после точки"
              onChange={e => { setForm({ ...form, amountUSDT: e.target.value }); setConfirmed(false); }} />
          </>}
          <TextField label="Причина / номер расчёта" value={form.reason} disabled={busy} inputProps={{ maxLength: 1000 }}
            onChange={e => { setForm({ ...form, reason: e.target.value }); setConfirmed(false); }} />
        </>}
        {dialog.reversalOf && <Alert severity="info">Полная сумма RUB вернётся в долг, USDT будет вычтена из статистики. Исходная операция сохранится в истории.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <FormControlLabel label="Подтверждаю запись расчёта" control={<Checkbox checked={confirmed} disabled={busy} onChange={e => setConfirmed(e.target.checked)} />} />
      </Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setDialog(null)}>Закрыть</Button>
        <Button disabled={busy || !system?.ready || !confirmed || (!pending && !valid)} onClick={save}>{pending ? 'Повторить сохранённый запрос' : 'Подтвердить'}</Button></DialogActions>
    </Dialog>}
  </Stack>;
}
